import { useMemo, useState } from 'react';
import {
  SimpleGrid, Group, Text, Badge, Table, Button, ActionIcon, Alert, Anchor, Tooltip, UnstyledButton,
  useComputedColorScheme, useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { MonthPickerInput } from '@mantine/dates';
import {
  IconWallet, IconCalculator, IconCash, IconHourglass, IconTrendingUp, IconChevronLeft, IconChevronRight,
  IconChevronUp, IconChevronDown, IconSelector, IconAlertTriangle, IconInfoCircle, IconFileSpreadsheet, IconUsers,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { StatCard } from '../components/StatCard';
import { SectionCard } from '../components/SectionCard';
import { TableToolbar } from '../components/TableToolbar';
import { TableSkeleton } from '../components/TableSkeleton';
import { EmptyState } from '../components/EmptyState';
import { MaoshBerishModal } from '../components/payroll/MaoshBerishModal';
import { formatSom } from '../lib/format';
import { monthToDate, normalizeMonth, thisMonth, ym, sanaLocale, OY_FORMATI } from '../lib/month';
import { eksportXlsx, type Ustun } from '../lib/xlsx';

/* `GET /payroll/oylik` javobi — backend `src/lib/maoshOylik.ts` dagi `OylikJavob` nusxasi. */
type OylikOgohlantirish = 'NO_SHIFT' | 'OPEN_LOG' | 'NO_LOGS' | 'NO_WORKDAYS';
/** Ogohlantirish belgisi: matn va tooltip kalitlari (`payrollPage.*`), rang. */
const OGOHLANTIRISH: Record<OylikOgohlantirish, { matn: string; izoh: string; rang: string }> = {
  NO_SHIFT: { matn: 'warnNoShift', izoh: 'warnNoShiftHint', rang: 'orange' },
  OPEN_LOG: { matn: 'warnOpenLog', izoh: 'warnOpenLogHint', rang: 'yellow' },
  NO_LOGS: { matn: 'warnNoLogs', izoh: 'warnNoLogsHint', rang: 'red' },
  // Oylik bor, kutilgan kun 0 — smena maosh kunlariga tushmaydi (sozlama xatosi, NO_SHIFT kabi)
  NO_WORKDAYS: { matn: 'warnNoWorkdays', izoh: 'warnNoWorkdaysHint', rang: 'orange' },
};
interface OylikQator {
  userId: string; fullName: string; role: string; isActive: boolean;
  oylik: number; kunlik: number;
  kutilganKunlar: number; otganKunlar: number; kelganKunlar: number; kelmaganKunlar: number; tashqariKunlar: number;
  kechDaqiqa: number; ertaDaqiqa: number;
  ushlangan: number; kelmaganPul: number; tuzatish: number; bugungiQism: number;
  yozilgan: number; berilgan: number; qolgan: number; prognoz: number;
  /** Bugun kelgan, smena hali tugamagan (xodim kartasidagi `fiks.bugunYakunlanmagan` bilan bir xil). */
  bugunYakunlanmagan?: boolean;
  ogohlantirishlar: OylikOgohlantirish[];
}
interface OylikJavob {
  month: string;
  qoida: { yoqilgan: boolean; fromMonth: string | null; ishKunlari: number; bayramKunlari: number; kechirimDaqiqa: number; maoshKunlari: string };
  bugun: string; hisoblanganAt: string;
  jami: { oylik: number; yozilgan: number; berilgan: number; qolgan: number; ortiqcha: number; prognoz: number; ushlangan: number; kelmaganPul: number; tuzatish: number };
  qatorlar: OylikQator[];
  maoshsizSoni: number;
}

/** O'qituvchi kartasi `/teachers/:id` da, qolgan xodimlar — `/staff/:id`. */
const OQITUVCHI_ROLLARI = ['TEACHER', 'HOMEROOM_TEACHER'];
const ROL_RANGI: Record<string, string> = { TEACHER: 'blue', HOMEROOM_TEACHER: 'grape', DIRECTOR: 'red', ADMIN: 'orange', ACCOUNTANT: 'teal', RECEPTION: 'cyan' };
/** Hisob vaqti doim maktab vaqtida (brauzer mintaqasidan qat'i nazar). */
const SOAT = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tashkent' });

type SaralashKalit = 'fullName' | 'yozilgan' | 'qolgan';
type Saralash = { kalit: SaralashKalit; tartib: 'asc' | 'desc' };

/** Manfiy summa: "−250 000 so'm" (ishora alohida — `formatSom` minusni defis bilan chiqaradi). */
const manfiy = (v: number) => `−${formatSom(Math.abs(v))}`;
/** Qolgan manfiy, chunki jarima ishlanganidan katta (yozilgan manfiy) — pul berilmagan bo'lishi mumkin,
 *  "ortiqcha berilgan" emas. Qator yorlig'i ham, jami bo'linishi ham shu shart bilan. */
const ushlanmaOshdi = (r: OylikQator) => r.qolgan < 0 && r.yozilgan < 0;

/** Bosiladigan sarlavha: shu ustun bo'yicha saralash (qayta bosilsa — teskari tartib). */
function SortTh({ faol, tartib, onClick, ta, style, children }: {
  faol: boolean; tartib: 'asc' | 'desc'; onClick: () => void; ta?: 'right'; style?: React.CSSProperties; children: React.ReactNode;
}) {
  const Ikonka = !faol ? IconSelector : tartib === 'asc' ? IconChevronUp : IconChevronDown;
  return (
    <Table.Th ta={ta} style={style}>
      <UnstyledButton onClick={onClick} style={{ fontWeight: 'inherit', fontSize: 'inherit' }}>
        <Group gap={4} wrap="nowrap" justify={ta === 'right' ? 'flex-end' : undefined}>
          {children}
          <Ikonka size={14} stroke={1.8} color={faol ? undefined : 'var(--mantine-color-dimmed)'} />
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

/**
 * MAOSH SAHIFASI (spec 2026-10-21 §6.1) — oylik (FIXED) xodimlar bo'yicha shu oy
 * yozilgan / berilgan / qolgan va oy oxiri prognozi. Bitta so'rov (`GET /payroll/oylik`),
 * har 60 s yangilanadi; sahifa yashirin bo'lsa to'xtaydi, qaytganda yangilanadi (ma'lumot
 * umumiy `staleTime` — 30 s dan eski bo'lsa).
 * Oy URL'da (`?oy=YYYY-MM`) — xodim kartasidan "orqaga" qaytilganda o'sha oy qoladi.
 */
export default function Payroll() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const telefon = !!useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const dark = useComputedColorScheme('light') === 'dark';
  /* Xodim ustuni gorizontal skrollda joyida turadi (QurilmaJadvali / JournalMatrix naqshi):
     Qolgan va "Maosh berish" ko'ringanda ham kimning qatori ekani ko'rinsin. Telefonda emas —
     tor ekranning ko'p qismini egallab qolardi. Fon — karta foni (yorug'da body, qorong'ida dark-6). */
  const ismUstuni: React.CSSProperties | undefined = telefon ? undefined : {
    position: 'sticky', left: 0, zIndex: 1,
    background: dark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-body)',
    boxShadow: 'inset -1px 0 0 var(--mantine-color-default-border)',
  };
  const [params, setParams] = useSearchParams();
  const month = normalizeMonth(params.get('oy')) || thisMonth();
  const setMonth = (m: string) => {
    const next = new URLSearchParams(params);
    next.set('oy', m);
    setParams(next, { replace: true });
  };
  const surish = (n: number) => {
    const d = monthToDate(month) ?? new Date();
    setMonth(ym(new Date(d.getFullYear(), d.getMonth() + n, 1)));
  };

  const [search, setSearch] = useState('');
  const [saralash, setSaralash] = useState<Saralash>({ kalit: 'fullName', tartib: 'asc' });
  // To'lov oynasi: qator yopilgandan keyin ham saqlanadi — yopilish animatsiyasida sarlavha o'zgarmasin
  const [tolov, setTolov] = useState<OylikQator | null>(null);
  const [tolovOchiq, setTolovOchiq] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['payroll-oylik', month],
    queryFn: async () => (await api.get<OylikJavob>('/payroll/oylik', { params: { month } })).data,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const qatorlar = useMemo(() => {
    const q = search.trim().toLowerCase();
    const r = (data?.qatorlar ?? []).filter((x) => !q || x.fullName.toLowerCase().includes(q));
    const k = saralash.tartib === 'asc' ? 1 : -1;
    return [...r].sort((a, b) =>
      saralash.kalit === 'fullName' ? k * a.fullName.localeCompare(b.fullName) : k * (a[saralash.kalit] - b[saralash.kalit]),
    );
  }, [data, search, saralash]);

  const saralashBos = (kalit: SaralashKalit) =>
    setSaralash((s) => (s.kalit === kalit ? { kalit, tartib: s.tartib === 'asc' ? 'desc' : 'asc' } : { kalit, tartib: kalit === 'fullName' ? 'asc' : 'desc' }));

  const ochish = (r: OylikQator) =>
    navigate(`${OQITUVCHI_ROLLARI.includes(r.role) ? '/teachers' : '/staff'}/${r.userId}?oy=${month}`);

  // Noma'lum kod (yangi server, eski front) — belgisiz o'tkazib yuborilmaydi, kodning o'zi chiqadi
  const ogohlantirishMatni = (w: OylikOgohlantirish) => (OGOHLANTIRISH[w] ? t(`payrollPage.${OGOHLANTIRISH[w].matn}`) : w);

  // Excel — jadval ustunlari, raqamlar son sifatida (Kunlar ikki ustunga bo'linadi)
  const ustunlar: Ustun<OylikQator>[] = [
    { key: 'fullName', label: t('payrollPage.staff') },
    { key: 'role', label: t('payrollPage.role'), get: (r) => t(`roles.${r.role}`) },
    { key: 'oylik', label: t('payrollPage.monthly') },
    { key: 'kunlik', label: t('payrollPage.daily') },
    { key: 'otganKunlar', label: t('payrollPage.passedDays') },
    { key: 'kutilganKunlar', label: t('payrollPage.expectedDays') },
    { key: 'kelganKunlar', label: t('payrollPage.came') },
    { key: 'kelmaganKunlar', label: t('payrollPage.absent') },
    { key: 'kelmaganPul', label: t('payrollPage.absentSum') },
    { key: 'kechDaqiqa', label: t('payrollPage.lateMin') },
    { key: 'ertaDaqiqa', label: t('payrollPage.earlyMin') },
    { key: 'ushlangan', label: t('payrollPage.withheld') },
    { key: 'tuzatish', label: t('payrollPage.adjustment') },
    { key: 'yozilgan', label: t('payrollPage.accrued') },
    { key: 'berilgan', label: t('payrollPage.paid') },
    { key: 'qolgan', label: t('payrollPage.remaining') },
    { key: 'ogohlantirishlar', label: t('payrollPage.warnings'), get: (r) => r.ogohlantirishlar.map(ogohlantirishMatni).join(', ') },
  ];
  const eksport = () => eksportXlsx({ filename: `maosh-${month}`, ustunlar, qatorlar });

  const jami = data?.jami;
  /* `jami.ortiqcha` (Σ max(0, −qolgan)) ikkiga — haqiqatan ortiqcha berilgan va ushlanma ishlanganidan
     oshgani (qatordagi yorliq bilan bir xil); butun maktab qatorlaridan, qidiruvdan qat'i nazar */
  const ortiqcha = useMemo(() => {
    let berilgan = 0;
    let ushlanma = 0;
    for (const r of data?.qatorlar ?? []) {
      if (r.qolgan >= 0) continue;
      if (ushlanmaOshdi(r)) ushlanma -= r.qolgan;
      else berilgan -= r.qolgan;
    }
    return { berilgan, ushlanma };
  }, [data]);
  const qolganIzoh = [
    ortiqcha.berilgan > 0 ? t('payrollPage.overpaidHint', { sum: formatSom(ortiqcha.berilgan) }) : '',
    ortiqcha.ushlanma > 0 ? t('payrollPage.overWithheldHint', { sum: formatSom(ortiqcha.ushlanma) }) : '',
  ].filter(Boolean).join(' · ');
  const sortProps = (kalit: SaralashKalit) => ({ faol: saralash.kalit === kalit, tartib: saralash.tartib, onClick: () => saralashBos(kalit) });

  return (
    <>
      <PageHeader
        title={t('payrollPage.title')}
        action={
          <Group gap="xs" wrap="wrap">
            <Group gap={4} wrap="nowrap">
              <ActionIcon variant="default" size="lg" aria-label={t('payrollPage.prevMonth')} onClick={() => surish(-1)}>
                <IconChevronLeft size={18} />
              </ActionIcon>
              <MonthPickerInput
                value={monthToDate(month)}
                onChange={(d) => d && setMonth(ym(d))}
                locale={sanaLocale(i18n.language)}
                valueFormat={OY_FORMATI}
                allowDeselect={false}
                w={170}
              />
              <ActionIcon variant="default" size="lg" aria-label={t('payrollPage.nextMonth')} onClick={() => surish(1)}>
                <IconChevronRight size={18} />
              </ActionIcon>
            </Group>
            <Button variant="light" leftSection={<IconFileSpreadsheet size={18} />} onClick={eksport} disabled={!qatorlar.length}>
              Excel
            </Button>
          </Group>
        }
      />

      <SimpleGrid cols={{ base: 2, md: 4 }} mb={6}>
        <StatCard icon={IconCalculator} label={t('payrollPage.accrued')} value={formatSom(jami?.yozilgan ?? 0)} />
        <StatCard icon={IconCash} color="green" label={t('payrollPage.paid')} value={formatSom(jami?.berilgan ?? 0)} />
        <StatCard
          icon={IconHourglass}
          color={(jami?.qolgan ?? 0) > 0 ? 'red' : 'teal'}
          label={t('payrollPage.remaining')}
          value={formatSom(jami?.qolgan ?? 0)}
          hint={qolganIzoh || undefined}
        />
        <StatCard icon={IconTrendingUp} color="grape" label={t('payrollPage.forecast')} value={formatSom(jami?.prognoz ?? 0)} />
      </SimpleGrid>
      <Text fz="xs" c="dimmed" mb="md" mih={18}>
        {data && t('payrollPage.meta', { kun: data.qoida.ishKunlari, daq: data.qoida.kechirimDaqiqa, vaqt: SOAT.format(new Date(data.hisoblanganAt)) })}
      </Text>

      {data && !data.qoida.yoqilgan && (
        <Alert color="yellow" variant="light" icon={<IconInfoCircle size={18} />} mb="md">
          {t('payrollPage.ruleOff')}
        </Alert>
      )}
      {data && data.maoshsizSoni > 0 && (
        <Alert color="blue" variant="light" icon={<IconUsers size={18} />} mb="md">
          <Group gap="xs">
            <Text fz="sm">{t('payrollPage.noSalary', { n: data.maoshsizSoni })}</Text>
            <Anchor component={Link} to="/staff" fz="sm" fw={600}>{t('payrollPage.toStaff')}</Anchor>
          </Group>
        </Alert>
      )}

      <TableToolbar search={search} onSearch={setSearch} searchPlaceholder={t('payrollPage.search')} />

      <SectionCard title={t('common.list')} icon={IconWallet} p={0}>
        {isLoading ? (
          <TableSkeleton rows={6} cols={6} />
        ) : isError && !data ? (
          <EmptyState
            icon={IconAlertTriangle}
            title={t('payrollPage.loadError')}
            action={<Button variant="light" onClick={() => refetch()}>{t('payrollPage.retry')}</Button>}
          />
        ) : !data?.qatorlar.length ? (
          <EmptyState icon={IconWallet} title={t('payrollPage.empty')} />
        ) : !qatorlar.length ? (
          <EmptyState icon={IconWallet} title={t('common.noMatch')} />
        ) : (
          <Table.ScrollContainer minWidth={1400}>
            <Table className="jadval-toliq" highlightOnHover verticalSpacing="sm" style={{ whiteSpace: 'nowrap' }}>
              <Table.Thead>
                <Table.Tr>
                  <SortTh {...sortProps('fullName')} style={ismUstuni}>{t('payrollPage.staff')}</SortTh>
                  <Table.Th ta="right">{t('payrollPage.monthly')}</Table.Th>
                  <Table.Th ta="right">{t('payrollPage.daily')}</Table.Th>
                  <Table.Th ta="center">{t('payrollPage.days')}</Table.Th>
                  <Table.Th ta="center">{t('payrollPage.came')}</Table.Th>
                  <Table.Th ta="center">{t('payrollPage.absent')}</Table.Th>
                  <Table.Th ta="center">{t('payrollPage.lateEarly')}</Table.Th>
                  <Table.Th ta="right">{t('payrollPage.withheld')}</Table.Th>
                  <Table.Th ta="right">{t('payrollPage.adjustment')}</Table.Th>
                  <SortTh {...sortProps('yozilgan')} ta="right">{t('payrollPage.accrued')}</SortTh>
                  <Table.Th ta="right">{t('payrollPage.paid')}</Table.Th>
                  <SortTh {...sortProps('qolgan')} ta="right">{t('payrollPage.remaining')}</SortTh>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {qatorlar.map((r) => (
                  <Table.Tr key={r.userId} style={{ cursor: 'pointer' }} onClick={() => ochish(r)}>
                    <Table.Td style={ismUstuni}>
                      <Text fw={600} fz="sm">{r.fullName}</Text>
                      <Group gap={4} mt={2} wrap="nowrap">
                        <Badge size="xs" variant="light" color={ROL_RANGI[r.role] ?? 'gray'}>{t(`roles.${r.role}`)}</Badge>
                        {r.ogohlantirishlar.map((w) => (
                          <Tooltip key={w} label={OGOHLANTIRISH[w] ? t(`payrollPage.${OGOHLANTIRISH[w].izoh}`) : w} multiline w={280} withArrow>
                            <Badge size="xs" variant="light" color={OGOHLANTIRISH[w]?.rang ?? 'gray'} leftSection={<IconAlertTriangle size={10} />}>
                              {ogohlantirishMatni(w)}
                            </Badge>
                          </Tooltip>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td ta="right">{formatSom(r.oylik)}</Table.Td>
                    <Table.Td ta="right">{formatSom(r.kunlik)}</Table.Td>
                    <Table.Td ta="center">
                      {r.tashqariKunlar > 0 ? (
                        <Tooltip label={t('payrollPage.outsideDays', { n: r.tashqariKunlar })} withArrow>
                          <Group gap={4} justify="center" wrap="nowrap">
                            <Text fz="sm">{r.otganKunlar}/{r.kutilganKunlar}</Text>
                            <IconInfoCircle size={14} color="var(--mantine-color-orange-6)" />
                          </Group>
                        </Tooltip>
                      ) : (
                        `${r.otganKunlar}/${r.kutilganKunlar}`
                      )}
                    </Table.Td>
                    <Table.Td ta="center">{r.kelganKunlar}</Table.Td>
                    <Table.Td ta="center">
                      <Text fz="sm" c={r.kelmaganKunlar > 0 ? 'red.7' : undefined}>{r.kelmaganKunlar}</Text>
                      {r.kelmaganPul > 0 && <Text fz="xs" c="dimmed">{formatSom(r.kelmaganPul)}</Text>}
                    </Table.Td>
                    <Table.Td ta="center">{t('payrollPage.lateEarlyValue', { kech: r.kechDaqiqa, erta: r.ertaDaqiqa })}</Table.Td>
                    <Table.Td ta="right" c={r.ushlangan > 0 ? 'red.7' : undefined}>{formatSom(r.ushlangan)}</Table.Td>
                    <Table.Td ta="right" c={r.tuzatish > 0 ? 'teal.7' : r.tuzatish < 0 ? 'red.7' : 'dimmed'}>
                      {r.tuzatish > 0 ? `+${formatSom(r.tuzatish)}` : r.tuzatish < 0 ? manfiy(r.tuzatish) : '—'}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text fw={700} fz="sm">{formatSom(r.yozilgan)}</Text>
                      {r.bugungiQism > 0 && <Text fz="xs" c="dimmed">{t('payrollPage.today', { sum: formatSom(r.bugungiQism) })}</Text>}
                    </Table.Td>
                    <Table.Td ta="right">{formatSom(r.berilgan)}</Table.Td>
                    <Table.Td ta="right">
                      {ushlanmaOshdi(r) ? (
                        // Jarima ishlanganidan katta (yozilgan manfiy) — pul berilmagan bo'lishi mumkin, "ortiqcha berilgan" emas
                        <>
                          <Text fw={600} fz="sm" c="orange.8">{manfiy(r.qolgan)}</Text>
                          <Text fz="xs" c="orange.8">{t('payrollPage.overWithheld')}</Text>
                        </>
                      ) : r.qolgan < 0 ? (
                        <Text fw={600} fz="sm" c="orange.8">{manfiy(r.qolgan)} {t('payrollPage.overpaid')}</Text>
                      ) : (
                        <Text fw={600} fz="sm" c={r.qolgan > 0 ? 'red.7' : 'teal.7'}>{formatSom(r.qolgan)}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Button size="xs" variant="light" leftSection={<IconCash size={14} />} onClick={(e) => { e.stopPropagation(); setTolov(r); setTolovOchiq(true); }}>
                        {t('payrollPage.pay')}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
              {jami && (
                <Table.Tfoot>
                  <Table.Tr style={{ borderTop: '2px solid var(--mantine-color-default-border)' }}>
                    <Table.Th style={ismUstuni}>{t('payrollPage.total')}</Table.Th>
                    <Table.Th ta="right">{formatSom(jami.oylik)}</Table.Th>
                    <Table.Th />
                    <Table.Th />
                    <Table.Th />
                    <Table.Th ta="center">{jami.kelmaganPul > 0 && <Text fz="xs" c="dimmed">{formatSom(jami.kelmaganPul)}</Text>}</Table.Th>
                    <Table.Th />
                    <Table.Th ta="right">{formatSom(jami.ushlangan)}</Table.Th>
                    <Table.Th ta="right">{jami.tuzatish > 0 ? `+${formatSom(jami.tuzatish)}` : jami.tuzatish < 0 ? manfiy(jami.tuzatish) : '—'}</Table.Th>
                    <Table.Th ta="right">{formatSom(jami.yozilgan)}</Table.Th>
                    <Table.Th ta="right">{formatSom(jami.berilgan)}</Table.Th>
                    {/* Σmax(0, qolgan); ortiqcha berilgani va ushlanma oshgani alohida — boshqa xodimning qarzini yemaydi */}
                    <Table.Th ta="right">
                      {formatSom(jami.qolgan)}
                      {ortiqcha.berilgan > 0 && (
                        <Text fz="xs" fw={500} c="orange.8">{t('payrollPage.overpaid')} {formatSom(ortiqcha.berilgan)}</Text>
                      )}
                      {ortiqcha.ushlanma > 0 && (
                        <Text fz="xs" fw={500} c="orange.8">{t('payrollPage.overWithheld')} {formatSom(ortiqcha.ushlanma)}</Text>
                      )}
                    </Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Tfoot>
              )}
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>

      <MaoshBerishModal
        opened={tolovOchiq}
        onClose={() => setTolovOchiq(false)}
        userId={tolov?.userId ?? ''}
        month={month}
        qolgan={tolov?.qolgan ?? 0}
        xodim={tolov?.fullName}
      />
    </>
  );
}
